import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordInput from '../components/PasswordInput';
import { TrendingUp, Mail, Lock, Loader2, Flame, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const { login } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // If the user landed here from a stale magic-link, tell them why in a friendly toast.
  useEffect(() => {
    const magicError = searchParams.get('magic_error');
    if (!magicError) return;
    const msg = language === 'fr'
      ? (magicError === 'expired'
          ? 'Ce lien de connexion rapide a expiré. Connectez-vous ci-dessous — vous n\'aurez à le faire qu\'une seule fois.'
          : 'Ce lien de connexion rapide n\'est plus valide. Connectez-vous ci-dessous.')
      : (magicError === 'expired'
          ? 'This one-click link has expired. Sign in below — once is enough.'
          : 'This one-click link is no longer valid. Sign in below.');
    toast.info(msg, { duration: 6000, id: 'magic-error-banner' });
  }, [searchParams, language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success(t('common.success'));
      // If the user came from the "Devenir Fondateur" CTA, route back to /subscribe
      // so the auto-claim useEffect there can pick up the stashed intent.
      let founderIntent = false;
      try { founderIntent = localStorage.getItem('tradex_founder_intent') === '1'; } catch (_e) { /* ignore */ }
      navigate(founderIntent ? '/subscribe' : '/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <TrendingUp className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold">{t('auth.loginTitle')}</h1>
          <p className="text-muted-foreground">{t('auth.loginSubtitle')}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="glass-card p-8 space-y-6" data-testid="login-form">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  placeholder="you@example.com"
                  className="pl-10 bg-secondary/50 border-white/10 focus:border-primary/50"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-primary hover:text-primary/80 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
                <PasswordInput
                  id="password"
                  data-testid="password-input"
                  placeholder="••••••••"
                  className="pl-10 bg-secondary/50 border-white/10 focus:border-primary/50"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            data-testid="submit-login"
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6 font-mono uppercase tracking-wider"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              t('auth.loginBtn')
            )}
          </Button>

          {/* Divider */}
          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-card px-3 text-xs text-muted-foreground uppercase tracking-widest">ou</span>
            </div>
          </div>

          {/* Google Sign-In — REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <Button
            type="button"
            variant="outline"
            data-testid="google-signin-btn"
            className="w-full bg-white text-gray-900 hover:bg-gray-100 border-white/20 py-6"
            onClick={() => {
              const redirectUrl = window.location.origin + '/dashboard';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuer avec Google
          </Button>
        </form>

        {/* Footer */}
        <p className="text-center text-muted-foreground">
          {t('auth.noAccount')}{' '}
          <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
            {t('nav.register')}
          </Link>
        </p>

        {/* CTA: See subscription plans (public, no account required) */}
        <Link to="/subscribe" className="block" data-testid="login-see-plans-btn">
          <div className="mt-2 flex items-center justify-between gap-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 transition-colors group">
            <div className="flex items-center gap-2.5">
              <Flame className="w-4 h-4 text-amber-400 flex-shrink-0" />
              <span className="text-sm text-foreground">
                {language === 'fr'
                  ? <>Voir nos abonnements <span className="text-muted-foreground">— offre fondateurs limitée</span></>
                  : <>View our plans <span className="text-muted-foreground">— limited founders offer</span></>}
              </span>
            </div>
            <ArrowRight className="w-4 h-4 text-amber-400 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Login;
